/*

	Grid Util

	Creates an array mapping of a set of coordinates that form the specified
	grid type.

	Origin is at the top left, with y increasing downward.

	See the individual grid types in generateGrid for more details on the grid type.
*/

const _ = require("lodash");

// Note: rows/columns are measure in number of DOTS
const generateGrid = function(gridType, props) {
	// rows/cols are measured in number of dots, not enclosed shapes
	const {scaleFactor = 1, flip = false, translation = [0, 0]} = props;
	let {rows, cols, pointsQty} = props;
	rows = parseInt(rows);
	cols = parseInt(cols);
	const type = gridType.toLowerCase();
	let isFlatPointMap = false; // for grids with points in top level of array

	let pointRect = [0, 0]; // returned point rectangle
	let pointMapArray = []; // returned array of indexed points

	if (type === "triangular" || type === "triangular1") {
		/*
			Grid with alternating rows offset to allow drawing equilateral triangles
			with horizontal sides.

			___ . .
			\ /
			 v . .

			. . . .

		*/

		// Rows: height in terms of height of a triangle (rows = 4 means 5 rows of dots)
		rows = rows || 4;
		// Cols: width of the bottom row in triangle sides (cols = 5 means five triangles
		//       could be drawn with horizontal bases at the bottom of the grid, i.e. 6 dots
		//       in the bottom row)
		cols = cols || 5;

		// Use w:h ratio of 8:7 to approximate ratio of 1:sqrt(0.75)
		// of a perfect equilateral triangle with one side horizontal
		const rowHeight = 7 / 8;

		// Include the offset in max width
		pointRect = [cols, rows * rowHeight];

		// Assign points to mapping by x coord, then y
		for (let x = 0; x < cols + 1; x++) {
			pointMapArray[x] = [];
			for (let y = 0; y < rows + 1; y++) {
				// Skip the last point in odd rows to avoid having points
				// "sticking out" from the right side
				if (x === cols && y % 2) continue;
				// Shift points in odd rows to the right
				const xOffset = y % 2 ? 0.5 : 0;
				pointMapArray[x][y] = [x + xOffset, y * rowHeight];
			}
		}
	} else if (type === "triangular2") {
		/*
			Grid with alternating rows offset to allow drawing equilateral triangles
			with vertical sides.

           _.           o
       _--- |
			<     |     o           o
       -___ |
			     -.           o

			o           o           o

			      o           o
		*/

		// Rows: height of grid in triangle sides
		rows = rows || 4;
		// Cols: width of grid in triangle widths (how many columns of triangles are there)
		cols = cols || 6;

		// Using a w:h ratio of 7:8 to approximate the the width/height ratio of an
		// equilateral triangle. 1 unit here is half the distance from one point to the next
		// in a row (the width of one triangle)
		const rowHeight = 4 / 7;

		// Include the offset in max width
		pointRect = [cols, rows * 2 * rowHeight];

		// Assign points to mapping by x coord, then y
		for (let x = 0; 2 * x <= cols; x++) {
			pointMapArray[x] = [];
			for (let y = 0; y < 2 * rows + 1; y++) {
				// Skip the last point in even rows when number of
				// columns is even to avoid having an extra column
				if (2 * x === cols && y % 2 === 0) continue;
				// Shift points in even rows to the right
				const xOffset = y % 2 ? 0 : 1;
				pointMapArray[x][y] = [2 * x + xOffset, y * rowHeight];
			}
		}
	} else if (type === "square") {
		// A rectangular grid. Notably not called "rectangle" because that's
		// being used for a rectangular grid with the interior points removed.

		// Rows: height of grid in squares
		rows = rows || 4;
		// Cols: width of grid in squares
		cols = cols || 4;

		pointRect = [cols, rows];

		for (let x = 0; x < cols + 1; x++) {
			pointMapArray[x] = [];
			for (let y = 0; y < rows + 1; y++) {
				pointMapArray[x][y] = [x, y];
			}
		}
	} else if (type === "tiltedsquare1" || type === "tiltedsquare2") {
		// Rectangular grid with evenly offset rows so square tilted at 45 degree
		// angle can be drawn. 1 unit is width between dots in a row/column

		// tiltedsquare1 has offset on odd rows, tiltedsquare2 has offset on even rows

		// Rows: width in large squares (with horizontal sides and point in center)
		rows = rows || 3;
		// Cols: height in large squares
		cols = cols || 3;

		pointRect = [cols, rows];

		for (let x = 0; x < cols + 1; x++) {
			pointMapArray[x] = [];
			for (let y = 0; y < 2 * rows + 1; y++) {
				let isOffset = y % 2;
				if (type === "tiltedsquare2") isOffset = !isOffset;
				// Skip ends of offset rows that would stick out
				if (x === cols && isOffset) continue;
				const xOffset = isOffset ? 0.5 : 0;
				pointMapArray[x][y] = [x + xOffset, y * 0.5];
			}
		}
	} else if (gridType === "rectangular" || gridType === "rectangle") {
		// Rectangular grid with interior points removed

		// Rows: width
		rows = rows || 4;
		// Cols: height
		cols = cols || 5;

		pointRect = [cols, rows];

		for (let x = 0; x < cols + 1; x++) {
			pointMapArray[x] = [];
			for (let y = 0; y < rows + 1; y++) {
				if (x !== 0 && x !== cols && y !== 0 && y !== rows) continue;
				pointMapArray[x][y] = [x, y];
			}
		}
	} else if (gridType === "circle" || gridType === "circular") {
		pointsQty = pointsQty || 12;
		isFlatPointMap = true;

		// Use unit circle
		pointRect = [2, 2];

		for (let i = 0; i < pointsQty; i++) {
			const angle = Math.PI * (0.5 - (2 / pointsQty) * i);
			pointMapArray[i] = [Math.cos(angle) + 1, Math.sin(angle) + 1];
		}
	}

	// Collect points into 1D array, discarding undefined values
	let points = _.compact(
		isFlatPointMap ? pointMapArray : _.flatten(pointMapArray)
	);

	pointRect = scale(pointRect, scaleFactor);
	points = scale(points, scaleFactor);
	pointMapArray = scale(pointMapArray, scaleFactor);

	if (flip) {
		points = invertY(pointRect, points);
		pointMapArray = invertY(pointRect, pointMapArray);
	}

	pointRect = translate(pointRect, translation);
	points = translate(points, translation);
	pointMapArray = translate(pointMapArray, translation);

	return {pointRect, points, pointMapArray};
};

// Find point coordinates (including nested coordinate arrays), and scale as necessary
const scale = function(arrayToScale, scaleFactor, precision = 4) {
	// Don't scale if not needed
	if (scaleFactor === 1) return arrayToScale;

	// Rounded to given place to avoid precision errors
	return modifyCoords(arrayToScale, coords =>
		_.map(coords, c => parseFloat((c * scaleFactor).toFixed(precision)))
	);
};

const modifyCoords = function(arrayToModify, modFunc) {
	// Safeguard against undefined entries in array
	if (!arrayToModify) return;

	// Check whether children are arrays or coordinate values
	if (_.findIndex(arrayToModify, subArray => _.isArray(subArray)) > -1) {
		// Child array found
		for (let i = 0; i < arrayToModify.length; i++) {
			// Keep digging
			arrayToModify[i] = modifyCoords(arrayToModify[i], modFunc);
		}
		return arrayToModify;
	} else {
		// arrayToModify is a set of coordinates, so modify it
		return modFunc(arrayToModify);
	}
};

const invertY = function(pointRect, arrayToInvert) {
	const height = pointRect[1];
	const flipFunc = coords => {
		const currentY = coords[1];
		const newY = height - currentY;
		// Using clone and assignment to [1] to be compatible with coord data
		// that includes extra values after the Y value (like some grids used
		// in PointConnectionLayout)
		const newCoords = _.clone(coords);
		newCoords[1] = newY;
		return newCoords;
	};
	return modifyCoords(arrayToInvert, flipFunc);
};

const translate = function(arrayToTranslate, translation) {
	if (_.isEqual([0, 0], translation)) return arrayToTranslate;
	return modifyCoords(arrayToTranslate, coords => [
		coords[0] + translation[0],
		coords[1] + translation[1],
	]);
};

module.exports = {
	generateGrid,
	scale,
};
